import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import HouseholdPlanModal from '../components/HouseholdPlanModal';

const { FiPlus, FiBox, FiCheckCircle, FiFilter, FiDollarSign, FiLayers } = FiIcons;

export default function Household() {
  const { state, dispatch } = useFamilyContext();
  const { householdPlans } = state;
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [filter, setFilter] = useState('active');

  const filteredPlans = householdPlans.filter(plan => {
    if (filter === 'active') return plan.status !== 'completed';
    if (filter === 'completed') return plan.status === 'completed';
    return true;
  });

  const handleAddPlan = (planData) => {
    if (editingPlan) {
      dispatch({ type: 'UPDATE_HOUSEHOLD_PLAN', payload: { ...editingPlan, ...planData } });
      setEditingPlan(null);
    } else {
      dispatch({ type: 'ADD_HOUSEHOLD_PLAN', payload: planData });
    }
  };

  const handleStatusChange = (plan, newStatus) => {
    if (newStatus === 'completed') {
      if (confirm('Projekt abgeschlossen! Möchten Sie dies als "Asset" im Inventar speichern?')) {
        dispatch({ 
          type: 'ADD_ASSET', 
          payload: { 
            name: plan.title, 
            category: plan.category, 
            purchaseDate: new Date().toISOString(),
            purchasePrice: plan.estimatedCost,
            notes: plan.notes
          } 
        });
      }
    }
    dispatch({ type: 'UPDATE_HOUSEHOLD_PLAN', payload: { ...plan, status: newStatus } });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Projekte</h1>
          <p className="text-gray-600 mt-2 dark:text-gray-400">Größere Anschaffungen und Projekte planen</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 mt-4 sm:mt-0"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Neues Projekt
        </button>
      </div>

      <div className="flex items-center space-x-4 mb-4">
        <button 
          onClick={() => setFilter('active')}
          className={`px-3 py-1 rounded-full text-sm ${filter === 'active' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
        >
          Offen
        </button>
        <button 
          onClick={() => setFilter('completed')}
          className={`px-3 py-1 rounded-full text-sm ${filter === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
        >
          Erledigt
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filteredPlans.map(plan => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col dark:bg-gray-800 dark:border-gray-700"
            >
              <div className="flex justify-between items-start mb-4">
                <div className={`p-2 rounded-lg ${plan.priority === 'high' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                  <SafeIcon icon={FiLayers} className="w-6 h-6" />
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  plan.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 
                  plan.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' : 
                  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                }`}>
                  {plan.priority === 'high' ? 'Hoch' : plan.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                </span>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-1 dark:text-white">{plan.title}</h3>
              <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{plan.category}</p>

              {plan.estimatedCost && (
                <div className="flex items-center text-gray-700 mb-2 dark:text-gray-300">
                  <SafeIcon icon={FiDollarSign} className="w-4 h-4 mr-1" />
                  <span className="font-medium">€ {plan.estimatedCost}</span>
                </div>
              )}

              <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
                 <button 
                   onClick={() => { setEditingPlan(plan); setShowModal(true); }}
                   className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                 >
                   Bearbeiten
                 </button>
                 {plan.status !== 'completed' && (
                   <button 
                     onClick={() => handleStatusChange(plan, 'completed')}
                     className="flex items-center text-sm text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                   >
                     <SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-1" />
                     Erledigen
                   </button>
                 )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {showModal && (
        <HouseholdPlanModal
          plan={editingPlan}
          onClose={() => setShowModal(false)}
          onSave={handleAddPlan}
        />
      )}
    </div>
  );
}